import {
  afterNextRender,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, map, switchMap } from 'rxjs/operators';
import { DataLayerService } from '../../services/analytics';
import { ToolbarService } from '../../core';
import { VerifiedUserComponent } from './components';
import { isPlatformBrowser } from '@angular/common';
import { HomeTab } from './constants';
import { HomeState } from './services/home.state';
import { HomeFacade } from './services/home.facade';
import { BannersFacade } from './services/banners.facade';
import { CmsFacade } from './services/cms.facade';
import { FlightOffersFacade } from './services/flight-offers.facade';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [HomeState, HomeFacade, BannersFacade, CmsFacade, FlightOffersFacade],
})
export class HomeComponent implements OnDestroy {
  private readonly facade = inject(HomeFacade);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly toolbarService = inject(ToolbarService);
  private readonly modal = inject(NgbModal);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly HomeTab = HomeTab;

  private readonly params = toSignal(this.route.params);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly selectedTab = this.facade.selectedTabIndex;

  constructor() {
    this.facade.initHome();

    effect(
      () => {
        const url = this.currentUrl();
        this.facade.updateTabByPath(url);
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const params = this.params();

      if (params?.['tab'] && isPlatformBrowser(this.platformId)) {
        this.dataLayerService.sendUserInfo();
        this.dataLayerService.sendVirtualPage(
          `/confirmacion/${params?.['tab']}`,
          'NMV: Confirmacion'
        );
      }
    });

    afterNextRender(() => this.listenToRouteActions());
  }

  private listenToRouteActions(): void {
    this.route.params
      .pipe(
        map((p) => p['id']),
        filter(Boolean),
        switchMap((id) => this.facade.confirmEmailByUserId(id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isSuccess) => {
        if (isSuccess) this.toggleConfirmationModal();
      });
  }

  tabSelectedByUser(tabIndex: HomeTab) {
    const path = this.facade.getPathByTabIndex(tabIndex);
    void this.router.navigate([path]);
  }

  private toggleConfirmationModal() {
    const modalRef = this.modal.open(VerifiedUserComponent, {
      windowClass: 'ModalUsuarioVerificado',
      ariaLabelledBy: 'ModalUsuarioVerificadoLabel',
      backdrop: false,
    });

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      void this.router.navigateByUrl('/');
      this.toolbarService.openLoginModal();
    });
  }

  ngOnDestroy() {
    this.modal.dismissAll();
  }
}
